import moment from 'moment';
import { formatDateUnified } from './dateUtils';

/**
 * Engineering Calculations Utility
 * Reusable functions for calculating engineering due dates and working days
 * accounting for PTO, holidays, and protected time.
 */

// Constants
export const ENGINEERING_REVIEW_DAYS = 2;
export const HOURS_PER_DAY = 8;

/**
 * Calculate engineering due date from review due date
 * Engineering Due Date = Review Due Date - ENGINEERING_REVIEW_DAYS
 * 
 * @param {string|Date|moment.Moment} reviewDueDate - The review due date
 * @param {number} offsetDays - Number of days before review (default: 2)
 * @returns {moment.Moment} Engineering due date
 */
export const calculateEngineeringDueDate = (
  reviewDueDate,
  offsetDays = ENGINEERING_REVIEW_DAYS,
  options = {}
) => {
  if (!reviewDueDate) return null;

  const { holidays = [], ptoEntries = [], engineerName = null } = options;

  // If we have holidays/PTO context, subtract working days; otherwise fallback to calendar days
  if ((holidays && holidays.length > 0) || (ptoEntries && ptoEntries.length > 0) || engineerName) {
    return subtractWorkingDays(reviewDueDate, offsetDays, { holidays, ptoEntries, engineerName });
  }

  return moment(reviewDueDate).subtract(offsetDays, 'days');
};

/**
 * Check if a given date is a PTO day for the provided engineer
 * Attempts to support multiple PTO entry schemas: UserName/EngineerName/engineerId and StartDate/EndDate
 */
const isEngineerPtoDay = (date, ptoEntries = [], engineerName = null) => {
  if (!ptoEntries || ptoEntries.length === 0 || !engineerName) return false;

  const dayDate = moment(date).format('YYYY-MM-DD');

  // Filter entries belonging to the engineer
  const entriesForEngineer = ptoEntries.filter(entry =>
    entry?.UserName === engineerName ||
    entry?.EngineerName === engineerName ||
    entry?.engineerId === engineerName
  );

  if (entriesForEngineer.length === 0) return false;

  // Use formatDateUnified when available; fallback to moment if it fails
  try {
    return entriesForEngineer.some(entry => {
      const startDateStr = formatDateUnified(entry.StartDate, 'iso');
      const endDateStr = formatDateUnified(entry.EndDate, 'iso');

      if (!startDateStr || !endDateStr) return false;
      return dayDate >= startDateStr && dayDate <= endDateStr;
    });
  } catch (e) {
    return entriesForEngineer.some(entry => {
      const startDateStr = moment(entry.StartDate).format('YYYY-MM-DD');
      const endDateStr = moment(entry.EndDate).format('YYYY-MM-DD');
      if (!startDateStr || !endDateStr) return false;
      return dayDate >= startDateStr && dayDate <= endDateStr;
    });
  }
};

/**
 * Subtract working days from a date, skipping weekends, holidays, and PTO for a given engineer.
 * Ensures the resulting date is a working day.
 *
 * @param {string|Date|moment.Moment} startDate - The starting date
 * @param {number} businessDays - Number of business days to subtract
 * @param {Object} ctx - Context containing holidays, ptoEntries, engineerName
 * @returns {moment.Moment} The resulting date (guaranteed to be a business day)
 */
export const subtractWorkingDays = (startDate, businessDays, ctx = {}) => {
  if (!startDate) return null;
  const { holidays = [], ptoEntries = [], engineerName = null } = ctx;

  let currentDate = moment(startDate);
  let daysSubtracted = 0;

  while (daysSubtracted < businessDays) {
    currentDate = currentDate.subtract(1, 'day');
    // Only count business days (not weekends, holidays, or PTO)
    if (
      !isWeekend(currentDate) &&
      !isHoliday(currentDate, holidays) &&
      !isEngineerPtoDay(currentDate, ptoEntries, engineerName)
    ) {
      daysSubtracted++;
    }
  }

  // Ensure the final date is not weekend/holiday/PTO; if it is, keep moving backwards
  while (
    isWeekend(currentDate) ||
    isHoliday(currentDate, holidays) ||
    isEngineerPtoDay(currentDate, ptoEntries, engineerName)
  ) {
    currentDate = currentDate.subtract(1, 'day');
  }

  return currentDate;
};

/**
 * Calculate working days backward from a due date, accounting for PTO, holidays, and protected time
 * 
 * @param {string|Date|moment.Moment} dueDate - The due date to work backwards from
 * @param {number} durationDays - Number of working days needed (can be fractional)
 * @param {string} engineerName - Name of the engineer for PTO/protected time lookup
 * @param {Object} options - Configuration options
 * @param {Function} options.isWeekend - Function to check if day is weekend
 * @param {Function} options.isDayInPtoPeriod - Function to check if day is in PTO period
 * @param {Function} options.isDayAHoliday - Function to check if day is a holiday
 * @param {Function} options.getAvailableHoursForDay - Function to get available hours for a day
 * @param {Function} options.getProtectedHoursForDay - Function to get protected hours for a day
 * @param {number} options.safetyLimit - Maximum days to process (default: 100)
 * @returns {Array} Array of day column objects with date, hours, and availability info
 */
export const calculateWorkingDaysBackward = (
  dueDate,
  durationDays,
  engineerName,
  {
    isWeekend,
    isDayInPtoPeriod,
    isDayAHoliday,
    getAvailableHoursForDay,
    getProtectedHoursForDay,
    getEngineerPtoEntries,
    safetyLimit = 100
  }
) => {
  if (!dueDate || !durationDays) return [];

  // Get PTO entries for this engineer
  const engineerPto = getEngineerPtoEntries(engineerName);

  const dayColumns = [];
  let currentDay = moment(dueDate);
  let hoursRemaining = durationDays * HOURS_PER_DAY; // Convert days to hours
  let daysProcessed = 0;

  // Work backwards from the due date
  while (hoursRemaining > 0 && daysProcessed < safetyLimit) {
    // Check if this is a working day
    if (
      !isWeekend(currentDay) &&
      !isDayInPtoPeriod(currentDay, engineerPto) &&
      !isDayAHoliday(currentDay)
    ) {
      const availableHours = getAvailableHoursForDay(currentDay, engineerName);

      if (availableHours > 0) {
        // This day can contribute to the task
        const hoursToUse = Math.min(availableHours, hoursRemaining);

        dayColumns.push({
          date: moment(currentDay),
          hours: hoursToUse,
          availableHours: availableHours,
          isFirstDay: hoursRemaining === durationDays * HOURS_PER_DAY,
          isLastDay: hoursToUse >= hoursRemaining,
          isPartialDay: hoursToUse < availableHours,
          protectedHours: getProtectedHoursForDay(currentDay, engineerName)
        });

        hoursRemaining -= hoursToUse;
      }
    }

    // Move to the previous day
    currentDay = currentDay.subtract(1, 'day');
    daysProcessed++;
  }

  return dayColumns.reverse(); // Return in chronological order (earliest to latest)
};

/**
 * Calculate actual duration accounting for available hours
 * Converts duration in days to hours and accounts for partial days
 * 
 * @param {number} durationDays - Duration in days (can be fractional)
 * @param {number} hoursPerDay - Hours per working day (default: 8)
 * @returns {number} Total hours needed
 */
export const calculateDurationInHours = (durationDays, hoursPerDay = HOURS_PER_DAY) => {
  if (!durationDays) return 0;
  return parseFloat(durationDays) * hoursPerDay;
};

/**
 * Calculate start date from end date and duration
 * This is a simplified version that counts calendar days backwards
 * 
 * @param {string|Date|moment.Moment} endDate - The end date
 * @param {number} durationDays - Number of days duration
 * @returns {moment.Moment} Calculated start date
 */
export const calculateStartDate = (endDate, durationDays) => {
  if (!endDate || !durationDays) return null;
  return moment(endDate).subtract(durationDays, 'days');
};

/**
 * Check if a date falls on a weekend
 * 
 * @param {string|Date|moment.Moment} date - The date to check
 * @returns {boolean} True if weekend (Saturday or Sunday)
 */
export const isWeekend = (date) => {
  const day = moment(date).day();
  return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
};

/**
 * Check if a date is a holiday
 * 
 * @param {string|Date|moment.Moment} date - The date to check
 * @param {Array} holidays - Array of holiday objects with HolidayDate property
 * @returns {boolean} True if the date is a holiday
 */
export const isHoliday = (date, holidays = []) => {
  if (!holidays || holidays.length === 0) return false;
  const dateStr = moment(date).format('YYYY-MM-DD');
  return holidays.some(holiday => {
    // Holidays are calendar dates, even when the API sends midnight UTC.
    const holidayDateStr = formatDateUnified(holiday.HolidayDate, 'iso');
    return holidayDateStr === dateStr;
  });
};

/**
 * Add business days to a date, skipping weekends and holidays
 * If the resulting date falls on a weekend or holiday, move forward to next business day
 * 
 * @param {string|Date|moment.Moment} startDate - The starting date
 * @param {number} businessDays - Number of business days to add
 * @param {Array} holidays - Optional array of holiday objects
 * @returns {moment.Moment} The resulting date (guaranteed to be a business day)
 */
export const addBusinessDays = (startDate, businessDays, holidays = []) => {
  if (!startDate) return null;
  
  let currentDate = moment(startDate);
  let daysAdded = 0;
  
  while (daysAdded < businessDays) {
    currentDate = currentDate.add(1, 'day');
    
    // Only count business days (not weekends or holidays)
    if (!isWeekend(currentDate) && !isHoliday(currentDate, holidays)) {
      daysAdded++;
    }
  }
  
  // Ensure the final date is not a weekend or holiday
  while (isWeekend(currentDate) || isHoliday(currentDate, holidays)) {
    currentDate = currentDate.add(1, 'day');
  }
  
  return currentDate;
};

/**
 * Ensure a date is a business day (not weekend or holiday)
 * If the date falls on a weekend or holiday, move forward to next business day
 * 
 * @param {string|Date|moment.Moment} date - The date to check
 * @param {Array} holidays - Optional array of holiday objects
 * @returns {moment.Moment} The date adjusted to be a business day
 */
export const ensureBusinessDay = (date, holidays = []) => {
  if (!date) return null;
  
  let resultDate = moment(date);
  
  // If it's a weekend or holiday, move to next business day
  while (isWeekend(resultDate) || isHoliday(resultDate, holidays)) {
    resultDate = resultDate.add(1, 'day');
  }
  
  return resultDate;
};
